import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLiveClasses } from "@/hooks/useComms";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";

interface RecordingSlide {
  id: number;
  title: string;
  content: string;
  image: string;
}

const slides: RecordingSlide[] = [
  {
    id: 1,
    title: "Konuya Giriş",
    content: "Bu derste işlenen temel kavramlar ve öğrenme hedefleri özetlenir.",
    image:
      "https://readdy.ai/api/search-image?query=Clean%20educational%20presentation%20slide%20introduction%20of%20a%20lesson%20with%20clear%20headings%20and%20bullet%20points%20on%20white%20background%2C%20minimal%20flat%20design%2C%20turquoise%20accent%20color%2C%20professional%20teaching%20slide&width=800&height=600&seq=rec-slide-1&orientation=landscape",
  },
  {
    id: 2,
    title: "Örnek Çözüm",
    content: "Öğretmenin derste çözdüğü örnek soru ve adım adım çözüm yaklaşımı.",
    image:
      "https://readdy.ai/api/search-image?query=Mathematical%20example%20problem%20solved%20step%20by%20step%20on%20a%20clean%20whiteboard%20style%20slide%2C%20black%20text%20with%20turquoise%20highlights%2C%20minimal%20educational%20design%2C%20white%20background&width=800&height=600&seq=rec-slide-2&orientation=landscape",
  },
  {
    id: 3,
    title: "Özet ve Tekrar",
    content: "Dersin kısa özeti ve evde tekrar için önerilen çalışma adımları.",
    image:
      "https://readdy.ai/api/search-image?query=Lesson%20summary%20and%20review%20checklist%20slide%20with%20checkmarks%20on%20white%20background%2C%20minimal%20flat%20educational%20design%2C%20turquoise%20accents%2C%20clean%20layout&width=800&height=600&seq=rec-slide-3&orientation=landscape",
  },
];

export default function CanliDersKayit() {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, reload } = useLiveClasses();
  const [currentSlide, setCurrentSlide] = useState(0);

  const cls = useMemo(() => (data ?? []).find((c) => c.id === id), [data, id]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  if (!cls) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-20">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <i className="ri-file-search-line text-2xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 text-xl font-bold text-foreground-950">Kayıt bulunamadı</h1>
        <p className="mt-2 text-sm text-foreground-600 max-w-md">
          Bu derse ait kayıt mevcut değil veya kaldırılmış olabilir.
        </p>
        <Link
          to="/ogrenci/canli-dersler"
          className="mt-6 whitespace-nowrap px-5 py-2.5 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
        >
          Canlı Derslere Dön
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700 bg-accent-100 px-2.5 py-1 rounded-full">
            <i className="ri-record-circle-line" aria-hidden="true"></i>
            Ders Kaydı
          </span>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
            {cls.title}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            {cls.subject} · {cls.grade} · {cls.duration} dk
          </p>
        </div>
        <Link
          to="/ogrenci/canli-dersler"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-background-200 bg-background-50 text-sm font-semibold text-foreground-700 hover:bg-background-100 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          Canlı Derslere Dön
        </Link>
      </div>

      {/* Kayıt oynatıcı */}
      <div className="bg-background-950 rounded-xl overflow-hidden">
        <div className="aspect-video flex flex-col">
          <div className="flex-1 flex items-center justify-center bg-background-900 relative">
            {cls.recordingUrl ? (
              <video
                src={cls.recordingUrl}
                controls
                className="w-full h-full object-contain"
                aria-label={cls.title}
              />
            ) : (
              <>
                <img
                  src={slides[currentSlide].image}
                  alt={slides[currentSlide].title}
                  className="max-h-full max-w-full object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 to-transparent pointer-events-none"></div>
              </>
            )}
          </div>
          {!cls.recordingUrl && (
            <div className="px-4 py-3 bg-background-950 flex items-center gap-4">
              <button
                type="button"
                onClick={() => setCurrentSlide((p) => Math.max(0, p - 1))}
                disabled={currentSlide === 0}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-800 text-background-100 hover:bg-background-700 disabled:opacity-40 transition-colors"
              >
                <i className="ri-skip-back-line" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                onClick={() => setCurrentSlide((p) => Math.min(slides.length - 1, p + 1))}
                disabled={currentSlide === slides.length - 1}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-800 text-background-100 hover:bg-background-700 disabled:opacity-40 transition-colors"
              >
                <i className="ri-skip-forward-line" aria-hidden="true"></i>
              </button>
              <div className="flex-1 h-1 rounded-full bg-background-800 overflow-hidden">
                <div
                  className="h-full rounded-full bg-accent-500 transition-all"
                  style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                />
              </div>
              <span className="text-xs text-background-400 whitespace-nowrap">
                {currentSlide + 1} / {slides.length}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Kayıt içeriği */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-background-50 rounded-xl border border-background-200 p-5">
          {cls.recordingUrl ? (
            <>
              <h2 className="font-heading font-semibold text-lg text-foreground-950">Ders Kaydı</h2>
              <p className="mt-2 text-sm md:text-base text-foreground-700 leading-relaxed">
                {cls.description || "Bu dersin video kaydını yukarıdan izleyebilirsin."}
              </p>
            </>
          ) : (
            <>
              <h2 className="font-heading font-semibold text-lg text-foreground-950">
                {slides[currentSlide].title}
              </h2>
              <p className="mt-2 text-sm md:text-base text-foreground-700 leading-relaxed">
                {slides[currentSlide].content}
              </p>
            </>
          )}
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-background-200 overflow-hidden shrink-0">
              <img src={cls.teacherAvatar} alt="" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-950">{cls.teacher}</p>
              <p className="text-xs text-foreground-500">Öğretmen</p>
            </div>
          </div>
          <div className="mt-4 space-y-2 text-sm text-foreground-600">
            <p className="flex items-center gap-2">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              {cls.date} · {cls.time}
            </p>
            <p className="flex items-center gap-2">
              <i className="ri-time-line" aria-hidden="true"></i>
              {cls.duration} dakika
            </p>
          </div>
          {cls.description && (
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed border-t border-background-200 pt-4">
              {cls.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}